import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import mapboxgl from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import { apiFetch } from "../api/api.js";
import { addTerrainLayer, SATELLITE_STYLE } from "../utils/mapTerrain.js";

// ─── Enum definitions ────────────────────────────────────────────────────────

const CLIMAT_CATEGORIES = [
  {
    value: "TEMPERATURE", label: "Температура", icon: "🌡️", color: "#ef4444",
    subs: [
      { value: "FROST", label: "Заморозки" },
      { value: "HEAT",  label: "Спека" },
    ],
  },
  {
    value: "MOISTURE", label: "Вологість", icon: "💧", color: "#3b82f6",
    subs: [
      { value: "DROUGHT", label: "Посуха" },
      { value: "HAIL",    label: "Град" },
    ],
  },
  {
    value: "WIND", label: "Вітер", icon: "💨", color: "#8b5cf6",
    subs: [
      { value: "STRONG_WIND", label: "Сильний вітер" },
      { value: "TORNADO",     label: "Смерч" },
    ],
  },
  {
    value: "SNOW", label: "Сніг / Лід", icon: "❄️", color: "#60a5fa",
    subs: [
      { value: "SNOWFALL",        label: "Снігопад" },
      { value: "SNOW_AVALANCHE",  label: "Сніговий замет" },
      { value: "SPRING_THAW",     label: "Весняна відлига" },
    ],
  },
  {
    value: "DISEASES_PESTS", label: "Хвороби і шкідники", icon: "🦠", color: "#f59e0b",
    subs: [
      { value: "FUNGAL_DISEASE",    label: "Грибкові хвороби" },
      { value: "PEST_REPRODUCTION", label: "Розмноження шкідників" },
      { value: "WEEDS",             label: "Бур'яни" },
    ],
  },
  {
    value: "SOLAR_RADIATION", label: "Сонячне випромінювання", icon: "☀️", color: "#f97316",
    subs: [
      { value: "INSUFFICIENT_LIGHT", label: "Недостатнє освітлення" },
      { value: "UV_RADIATION",       label: "УФ-випромінювання" },
    ],
  },
  {
    value: "ATMOSPHERE_PHENOMENA", label: "Атмосферні явища", icon: "⛈️", color: "#6366f1",
    subs: [
      { value: "THUNDERSTORM", label: "Гроза" },
      { value: "FOG",          label: "Туман" },
    ],
  },
  {
    value: "EXTREME_PHENOMENA", label: "Екстремальні явища", icon: "🌪️", color: "#dc2626",
    subs: [
      { value: "DUST_STORM", label: "Пилова буря" },
      { value: "FLOOD",      label: "Повінь" },
    ],
  },
];

const RISK_LEVELS = [
  { value: "LOW",    label: "Низький",  color: "#22c55e", bg: "rgba(34,197,94,.12)"  },
  { value: "MEDIUM", label: "Середній", color: "#f59e0b", bg: "rgba(245,158,11,.12)" },
  { value: "HIGH",   label: "Високий",  color: "#ef4444", bg: "rgba(239,68,68,.12)"  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getSubInfo(subType) {
  for (const cat of CLIMAT_CATEGORIES) {
    const sub = cat.subs.find(s => s.value === subType);
    if (sub) return { ...sub, catValue: cat.value, catLabel: cat.label, catIcon: cat.icon, color: cat.color };
  }
  return { value: subType, label: subType, catValue: null, catLabel: "—", catIcon: "📌", color: "#94a3b8" };
}

function getRiskInfo(level) {
  return RISK_LEVELS.find(r => r.value === level)
    ?? { value: level, label: level, color: "#94a3b8", bg: "rgba(148,163,184,.12)" };
}

function formatDate(raw) {
  if (!raw) return null;
  try {
    const d = new Date(raw);
    if (isNaN(d)) return null;
    return d.toLocaleDateString("uk-UA", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return null; }
}

function ensureToken() {
  const t = mapboxgl.accessToken || process.env.REACT_APP_MAPBOX_TOKEN || "";
  if (t && !mapboxgl.accessToken) mapboxgl.accessToken = t;
  return !!mapboxgl.accessToken;
}

function getFieldCenter(geoJson) {
  if (!geoJson) return [31, 48];
  const geo = geoJson.type === "Feature" ? geoJson.geometry : geoJson;
  let coords = [];
  if (geo?.type === "Polygon")      coords = geo.coordinates?.flat(1) ?? [];
  if (geo?.type === "MultiPolygon") coords = geo.coordinates?.flat(2) ?? [];
  if (!coords.length) return [31, 48];
  const lngs = coords.map(c => c[0]);
  const lats  = coords.map(c => c[1]);
  return [(Math.min(...lngs) + Math.max(...lngs)) / 2, (Math.min(...lats) + Math.max(...lats)) / 2];
}

function tryParseJson(v) {
  if (!v) return null;
  if (typeof v === "object") return v;
  try {
    const p = JSON.parse(v);
    // handle double-encoded strings: "\"{ \\\"type\\\": ... }\""
    if (typeof p === "string") return tryParseJson(p);
    return p;
  } catch { return null; }
}

const SKIP_KEYS = new Set(["id", "name", "description", "ha", "fieldId", "climatSubType", "riskLevel", "createdAt", "updatedAt", "field"]);

function extractGeometry(zone) {
  // Preferred field names first
  const candidates = ["geometry", "geometryJson", "geom", "geoJson", "polygon", "shape", "area", "zone", "coordinates"];
  for (const key of candidates) {
    if (zone[key] == null) continue;
    const parsed = tryParseJson(zone[key]);
    const g = unwrapGeom(parsed);
    if (g) { console.log(`[CZ] geometry found in field "${key}" for zone "${zone.name}"`); return g; }
  }
  // Last resort: scan every field we haven't tried
  for (const key of Object.keys(zone)) {
    if (SKIP_KEYS.has(key) || candidates.includes(key)) continue;
    const parsed = tryParseJson(zone[key]);
    const g = unwrapGeom(parsed);
    if (g) { console.log(`[CZ] geometry found in fallback field "${key}" for zone "${zone.name}"`); return g; }
  }
  console.warn(`[CZ] NO geometry for zone id=${zone.id} name="${zone.name}". Zone keys:`, Object.keys(zone));
  return null;
}

function unwrapGeom(obj) {
  if (!obj) return null;
  let g = obj;
  if (g.type === "Feature")            g = g.geometry;
  if (g?.type === "FeatureCollection") g = g.features?.[0]?.geometry;
  if (g?.type && Array.isArray(g.coordinates)) return g;
  return null;
}

function haversineM(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function fmtDistCZ(m) {
  return m < 1000 ? `${Math.round(m)} м` : `${(m / 1000).toFixed(2)} км`;
}

// ─── ClimateNavigateMap ───────────────────────────────────────────────────────

function ClimateNavigateMap({ fieldGeoJson, target, onClose }) {
  const containerRef = useRef(null);
  const mapRef       = useRef(null);
  const posMarkerRef = useRef(null);
  const watchIdRef   = useRef(null);
  const userPosRef   = useRef(null);
  const [userPos, setUserPos] = useState(null);
  const [posErr,  setPosErr]  = useState(false);

  useEffect(() => {
    if (!containerRef.current || !ensureToken()) return;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: SATELLITE_STYLE,
      center: [target.lng, target.lat],
      zoom: 15,
    });
    map.addControl(new mapboxgl.NavigationControl(), "top-right");
    mapRef.current = map;

    map.on("load", () => {
      addTerrainLayer(map);

      if (fieldGeoJson) {
        map.addSource("cnm-field", { type: "geojson", data: fieldGeoJson });
        map.addLayer({ id: "cnm-field-fill", type: "fill",   source: "cnm-field", paint: { "fill-color": "#22c55e", "fill-opacity": 0.08 } });
        map.addLayer({ id: "cnm-field-line", type: "line",   source: "cnm-field", paint: { "line-color": "#22c55e", "line-width": 2 } });
      }

      if (target.polygon) {
        map.addSource("cnm-zone", { type: "geojson", data: { type: "Feature", geometry: target.polygon } });
        map.addLayer({ id: "cnm-zone-fill", type: "fill", source: "cnm-zone",
          paint: { "fill-color": target.color || "#ef4444", "fill-opacity": 0.20 } });
        map.addLayer({ id: "cnm-zone-line", type: "line", source: "cnm-zone",
          paint: { "line-color": target.color || "#ef4444", "line-width": 2.5 } });

        const coords = target.polygon.type === "Polygon"
          ? target.polygon.coordinates.flat(1)
          : (target.polygon.coordinates?.flat(2) ?? []);
        if (coords.length > 0) {
          const lngs = coords.map(c => c[0]);
          const lats  = coords.map(c => c[1]);
          map.fitBounds(
            [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
            { padding: 80, duration: 600 }
          );
        }
      }

      const pinEl = document.createElement("div");
      pinEl.style.cssText = `width:18px;height:18px;border-radius:50%;background:${target.color||"#ef4444"};border:3px solid #fff;box-shadow:0 0 0 4px ${target.color||"#ef4444"}44,0 3px 12px rgba(0,0,0,.45);`;
      new mapboxgl.Marker({ element: pinEl, anchor: "center" }).setLngLat([target.lng, target.lat]).addTo(map);

      map.addSource("cnm-line", { type: "geojson", data: { type: "Feature", geometry: { type: "LineString", coordinates: [] } } });
      map.addLayer({ id: "cnm-line-layer", type: "line", source: "cnm-line",
        paint: { "line-color": "#3b82f6", "line-width": 2.5, "line-dasharray": [6, 4], "line-opacity": 0.75 } });

      if (userPosRef.current) applyPos(map, userPosRef.current);
    });

    return () => {
      if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
      posMarkerRef.current?.remove();
      try { map.remove(); } catch {}
      mapRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function applyPos(map, pos) {
    if (!map || !map.isStyleLoaded()) return;
    if (posMarkerRef.current) {
      posMarkerRef.current.setLngLat([pos.lng, pos.lat]);
    } else {
      const el = document.createElement("div");
      el.style.cssText = `width:18px;height:18px;border-radius:50%;background:#3b82f6;border:3px solid #fff;box-shadow:0 0 0 5px rgba(59,130,246,.25),0 2px 8px rgba(0,0,0,.3);`;
      posMarkerRef.current = new mapboxgl.Marker({ element: el, anchor: "center" })
        .setLngLat([pos.lng, pos.lat]).addTo(map);
    }
    if (map.getSource("cnm-line")) {
      map.getSource("cnm-line").setData({
        type: "Feature",
        geometry: { type: "LineString", coordinates: [[pos.lng, pos.lat], [target.lng, target.lat]] },
      });
    }
  }

  useEffect(() => {
    if (!navigator.geolocation) { setPosErr(true); return; }
    watchIdRef.current = navigator.geolocation.watchPosition(
      pos => {
        const p = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        userPosRef.current = p;
        setUserPos(p);
        applyPos(mapRef.current, p);
      },
      () => setPosErr(true),
      { enableHighAccuracy: true, maximumAge: 4000, timeout: 15000 }
    );
    return () => { if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dist = userPos ? haversineM(userPos.lat, userPos.lng, target.lat, target.lng) : null;
  const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${target.lat},${target.lng}`;

  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 8000, display: "flex", flexDirection: "column", background: "var(--bg,#f8fafc)" }}>
      <div style={{ background: "var(--panel,#fff)", borderBottom: "1px solid var(--border,#e5e7eb)", padding: "10px 14px", display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
        <button type="button" onClick={onClose} style={{ background: "transparent", border: "1px solid var(--border,#e5e7eb)", borderRadius: 8, padding: "7px 13px", cursor: "pointer", fontSize: 13, fontWeight: 700, color: "var(--text,#111)", flexShrink: 0 }}>← Назад</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 14, color: "var(--text,#111)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            🌦 {target.label}
          </div>
          <div style={{ fontSize: 10, color: "var(--muted,#9ca3af)", fontFamily: "monospace" }}>
            {target.lat.toFixed(5)}, {target.lng.toFixed(5)}
          </div>
        </div>
        {dist !== null && (
          <div style={{ background: "rgba(59,130,246,.1)", border: "1px solid rgba(59,130,246,.3)", borderRadius: 10, padding: "5px 12px", textAlign: "center", flexShrink: 0 }}>
            <div style={{ fontWeight: 900, fontSize: 16, color: "#2563eb" }}>{fmtDistCZ(dist)}</div>
            <div style={{ fontSize: 9, fontWeight: 700, color: "#2563eb", textTransform: "uppercase", letterSpacing: ".04em" }}>від вас</div>
          </div>
        )}
        <a href={mapsUrl} target="_blank" rel="noopener noreferrer" style={{ flexShrink: 0, background: "#2563eb", color: "#fff", borderRadius: 9, padding: "8px 13px", fontSize: 12, fontWeight: 700, textDecoration: "none", display: "flex", alignItems: "center", gap: 5, boxShadow: "0 2px 8px rgba(37,99,235,.35)" }}>
          🧭 Маршрут
        </a>
      </div>

      {!userPos && (
        <div style={{ background: posErr ? "rgba(239,68,68,.07)" : "rgba(59,130,246,.06)", borderBottom: "1px solid " + (posErr ? "rgba(239,68,68,.2)" : "rgba(59,130,246,.15)"), padding: "7px 14px", fontSize: 11, flexShrink: 0, color: posErr ? "#dc2626" : "#2563eb", fontWeight: 600 }}>
          {posErr ? "⚠️ Неможливо визначити позицію. Перевірте дозвіл на геолокацію в браузері." : "📡 Визначаю вашу позицію…"}
        </div>
      )}

      <div ref={containerRef} style={{ flex: 1, minHeight: 0 }} />

      <div style={{ background: "var(--panel,#fff)", borderTop: "1px solid var(--border,#e5e7eb)", padding: "8px 14px", flexShrink: 0, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 12, height: 3, background: target.color || "#ef4444", borderRadius: 2 }} />
          <span style={{ fontSize: 11, color: "var(--muted,#6b7280)" }}>Кліматична зона · {target.label}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#3b82f6", border: "2px solid #fff", boxShadow: "0 0 0 2px rgba(59,130,246,.3)" }} />
          <span style={{ fontSize: 11, color: "var(--muted,#6b7280)" }}>{userPos ? "Ваша позиція" : "Очікую позицію…"}</span>
        </div>
        {userPos && (
          <div style={{ marginLeft: "auto", fontSize: 10, color: "var(--muted,#9ca3af)", fontFamily: "monospace" }}>
            {userPos.lat.toFixed(5)}, {userPos.lng.toFixed(5)}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// ─── ViewMap ─────────────────────────────────────────────────────────────────

function ViewMap({ fieldGeoJson, selectedZoneData, onRetry }) {
  const ref    = useRef(null);
  const mapRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  // ── Init map with field boundary only (no placeholder zone sources)
  useEffect(() => {
    if (!ref.current || !fieldGeoJson) return;
    if (!ensureToken()) return;

    const center = getFieldCenter(fieldGeoJson);
    const map = new mapboxgl.Map({ container: ref.current, style: SATELLITE_STYLE, center, zoom: 13 });
    mapRef.current = map;

    map.on("load", () => {
      addTerrainLayer(map);
      map.addSource("czv-field", { type: "geojson", data: fieldGeoJson });
      map.addLayer({ id: "czv-field-fill", type: "fill",   source: "czv-field", paint: { "fill-color": "#22c55e", "fill-opacity": 0.08 } });
      map.addLayer({ id: "czv-field-line", type: "line",   source: "czv-field", paint: { "line-color": "#22c55e", "line-width": 2 } });
      map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
      setMapLoaded(true);
    });

    return () => {
      setMapLoaded(false);
      try { map.remove(); } catch {}
      mapRef.current = null;
    };
  }, [fieldGeoJson]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Draw selected zone: remove + re-add source/layers on every change
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;
    const map = mapRef.current;

    // Always remove old zone layers/source first
    ["czv-sel-ln", "czv-sel-fi"].forEach(id => { try { if (map.getLayer(id)) map.removeLayer(id); } catch {} });
    try { if (map.getSource("czv-sel")) map.removeSource("czv-sel"); } catch {}

    if (!selectedZoneData?.geom) return;

    const { geom, info, name } = selectedZoneData;
    const color = info?.color ?? "#ef4444";

    console.log("[CZ] ViewMap: drawing zone", name, "color", color, "geom type", geom.type);

    try {
      map.addSource("czv-sel", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [{ type: "Feature", geometry: geom, properties: { name: name ?? "" } }] },
      });
      map.addLayer({ id: "czv-sel-fi", type: "fill", source: "czv-sel", paint: { "fill-color": color, "fill-opacity": 0.35 } });
      map.addLayer({ id: "czv-sel-ln", type: "line", source: "czv-sel", paint: { "line-color": color, "line-width": 4 } });
    } catch (err) {
      console.error("[CZ] ViewMap addLayer error:", err);
      return;
    }

    try {
      const coords = geom.type === "Polygon" ? geom.coordinates.flat(1) : (geom.coordinates?.flat(2) ?? []);
      if (coords.length > 1) {
        const lngs = coords.map(c => c[0]);
        const lats  = coords.map(c => c[1]);
        map.fitBounds(
          [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
          { padding: 80, maxZoom: 17, duration: 500 }
        );
      }
    } catch (err) {
      console.error("[CZ] ViewMap fitBounds error:", err);
    }
  }, [selectedZoneData, mapLoaded]);

  return (
    <div style={{ position: "relative", height: "100%" }}>
      <div ref={ref} style={{ width: "100%", height: "100%", borderRadius: 12, overflow: "hidden", border: "1px solid var(--border)" }} />
      {!selectedZoneData && (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none", borderRadius: 12 }}>
          <div style={{ background: "rgba(0,0,0,.55)", backdropFilter: "blur(4px)", borderRadius: 12, padding: "12px 20px", textAlign: "center", maxWidth: 220 }}>
            <div style={{ color: "#fff", fontSize: 13, fontWeight: 700, marginBottom: 4 }}>← Оберіть зону зі списку</div>
            <div style={{ color: "rgba(255,255,255,.6)", fontSize: 11 }}>Натисніть на картку щоб побачити зону</div>
          </div>
        </div>
      )}
      {selectedZoneData && !selectedZoneData.geom && (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 12, pointerEvents: "none" }}>
          <div style={{ background: "rgba(0,0,0,.65)", backdropFilter: "blur(6px)", borderRadius: 14, padding: "16px 20px", textAlign: "center", maxWidth: 260, pointerEvents: "auto" }}>
            <div style={{ color: "#fbbf24", fontSize: 13, fontWeight: 700, marginBottom: 6 }}>⚠ Геометрія не відображається</div>
            <div style={{ color: "rgba(255,255,255,.75)", fontSize: 11, lineHeight: 1.5, marginBottom: onRetry ? 12 : 0 }}>
              Не вдалося завантажити контур зони. Спробуйте ще раз або оновіть сторінку.
            </div>
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                style={{
                  background: "#f59e0b", color: "#000", border: "none",
                  borderRadius: 8, padding: "7px 18px",
                  fontSize: 12, fontWeight: 800, cursor: "pointer",
                  width: "100%",
                }}
              >
                ↻ Спробувати ще раз
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── DrawMap ─────────────────────────────────────────────────────────────────

function DrawMap({ fieldGeoJson, onGeometryChange, accentColor = "#ef4444" }) {
  const ref     = useRef(null);
  const mapRef  = useRef(null);
  const drawRef = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    if (!ensureToken()) return;

    const center = fieldGeoJson ? getFieldCenter(fieldGeoJson) : [31, 48];
    const map = new mapboxgl.Map({
      container: ref.current,
      style: SATELLITE_STYLE,
      center,
      zoom: 14,
    });
    mapRef.current = map;

    const c = accentColor;
    const draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: { polygon: true, trash: true },
      styles: [
        { id: "gl-draw-polygon-fill",         type: "fill",   filter: ["all", ["==", "$type", "Polygon"],  ["!=", "mode", "static"]], paint: { "fill-color": c, "fill-opacity": 0.22 } },
        { id: "gl-draw-polygon-stroke",       type: "line",   filter: ["all", ["==", "$type", "Polygon"],  ["!=", "mode", "static"]], paint: { "line-color": c, "line-width": 2.5 } },
        { id: "gl-draw-polygon-midpoint",     type: "circle", filter: ["all", ["==", "$type", "Point"],    ["==", "meta", "midpoint"]], paint: { "circle-radius": 4, "circle-color": c } },
        { id: "gl-draw-polygon-point-outer",  type: "circle", filter: ["all", ["==", "$type", "Point"],    ["!=", "meta", "midpoint"]], paint: { "circle-radius": 6, "circle-color": "#fff", "circle-stroke-width": 2, "circle-stroke-color": c } },
      ],
    });
    drawRef.current = draw;
    map.addControl(draw, "top-left");
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");

    map.on("load", () => {
      addTerrainLayer(map);
      if (fieldGeoJson) {
        map.addSource("czd-field", { type: "geojson", data: fieldGeoJson });
        map.addLayer({ id: "czd-field-fill", type: "fill", source: "czd-field", paint: { "fill-color": "#22c55e", "fill-opacity": 0.08 } });
        map.addLayer({ id: "czd-field-line", type: "line", source: "czd-field", paint: { "line-color": "#22c55e", "line-width": 2.5 } });
      }
    });

    function emitChange() {
      const fc   = draw.getAll();
      const geom = fc?.features?.[0]?.geometry ?? null;
      onGeometryChange?.(geom);
    }
    map.on("draw.create", emitChange);
    map.on("draw.update", emitChange);
    map.on("draw.delete", emitChange);

    return () => {
      try { map.remove(); } catch {}
      mapRef.current = null;
      drawRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ position: "relative" }}>
      <div ref={ref} style={{ width: "100%", height: 280, borderRadius: 12, overflow: "hidden" }} />
      <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
        🖊 Натисніть інструмент полігону (зліва) і намалюйте зону на полі
      </div>
    </div>
  );
}

// ─── ZoneCard ────────────────────────────────────────────────────────────────

function ZoneCard({ zone, selected, onSelect, onDelete, onNavigate }) {
  const sub     = getSubInfo(zone.climatSubType);
  const risk    = getRiskInfo(zone.riskLevel);
  const hasGeom = !!zone._cachedGeom || !!extractGeometry(zone);

  return (
    <div
      onClick={() => onSelect?.(zone.id)}
      style={{
        border: `1.5px solid ${selected ? sub.color : "var(--border,#e5e7eb)"}`,
        borderLeft: `4px solid ${sub.color}`,
        borderRadius: 10,
        background: selected ? `${sub.color}0e` : "var(--panel,#fff)",
        padding: "10px 12px",
        cursor: "pointer",
        boxShadow: selected ? `0 0 0 3px ${sub.color}22` : "0 1px 3px rgba(0,0,0,.06)",
        transition: "all .15s",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 9 }}>

        {/* Category icon badge */}
        <div style={{
          width: 32, height: 32, borderRadius: 9, flexShrink: 0,
          background: `${sub.color}1a`, border: `1.5px solid ${sub.color}50`,
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
        }}>
          {sub.catIcon}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Badges row */}
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 3 }}>
            <span style={{
              fontSize: 10, padding: "1px 6px", borderRadius: 20, fontWeight: 700,
              background: `${sub.color}18`, border: `1px solid ${sub.color}40`, color: sub.color,
            }}>{sub.catLabel}</span>
            <span style={{
              fontSize: 10, padding: "1px 6px", borderRadius: 20, fontWeight: 700,
              background: risk.bg, border: `1px solid ${risk.color}40`, color: risk.color,
            }}>⚠ {risk.label}</span>
            {zone.ha && (
              <span style={{
                fontSize: 10, padding: "1px 6px", borderRadius: 20, fontWeight: 600,
                background: "rgba(100,116,139,.08)", border: "1px solid rgba(100,116,139,.20)", color: "var(--muted)",
              }}>📐 {Number(zone.ha).toFixed(1)} га</span>
            )}
            <span style={{
              fontSize: 10, padding: "1px 6px", borderRadius: 20, fontWeight: 600,
              background: hasGeom ? "rgba(34,197,94,.10)" : "rgba(100,116,139,.08)",
              border: hasGeom ? "1px solid rgba(34,197,94,.30)" : "1px solid rgba(100,116,139,.20)",
              color: hasGeom ? "#16a34a" : "var(--muted)",
            }}>{hasGeom ? "📍 Карта" : "○ Без карти"}</span>
          </div>

          <div style={{ fontWeight: 700, fontSize: 13, color: "var(--text,#111)", lineHeight: 1.3 }}>{zone.name}</div>
          <div style={{ fontSize: 11, fontWeight: 600, color: sub.color, marginTop: 1 }}>{sub.label}</div>

          {zone.description && zone.description !== "—" && (
            <p style={{ margin: "3px 0 0", fontSize: 11, color: "var(--muted,#6b7280)", lineHeight: 1.4 }}>
              {zone.description}
            </p>
          )}

          {formatDate(zone.createdAt) && (
            <div style={{ fontSize: 10, color: "var(--muted,#9ca3af)", marginTop: 3 }}>
              🗓 {formatDate(zone.createdAt)}
            </div>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 4, flexShrink: 0 }}>
          {onNavigate && (
            <button
              type="button"
              onClick={e => { e.stopPropagation(); onNavigate(zone); }}
              style={{
                background: "rgba(37,99,235,.06)", color: "#2563eb",
                border: "1px solid rgba(37,99,235,.18)", padding: "4px 7px",
                borderRadius: 7, cursor: "pointer", fontSize: 11, lineHeight: 1,
              }}
            >🧭</button>
          )}
          <button
            type="button"
            onClick={e => { e.stopPropagation(); onDelete?.(zone.id); }}
            style={{
              background: "rgba(239,68,68,.06)", color: "#ef4444",
              border: "1px solid rgba(239,68,68,.18)", padding: "4px 7px",
              borderRadius: 7, cursor: "pointer", fontSize: 11, lineHeight: 1,
            }}
          >🗑</button>
        </div>
      </div>
    </div>
  );
}

// ─── SubTypePicker ────────────────────────────────────────────────────────────

function SubTypePicker({ value, onChange }) {
  const [openCat, setOpenCat] = useState(() => {
    if (!value) return null;
    for (const cat of CLIMAT_CATEGORIES) {
      if (cat.subs.some(s => s.value === value)) return cat.value;
    }
    return null;
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      {CLIMAT_CATEGORIES.map(cat => {
        const isOpen = openCat === cat.value;
        const hasSel = cat.subs.some(s => s.value === value);

        return (
          <div
            key={cat.value}
            style={{
              border: `1.5px solid ${hasSel ? cat.color : "var(--border,#e5e7eb)"}`,
              borderLeft: `3px solid ${cat.color}`,
              borderRadius: 9, overflow: "hidden",
            }}
          >
            <button
              type="button"
              onClick={() => setOpenCat(isOpen ? null : cat.value)}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "7px 11px",
                background: hasSel ? `${cat.color}10` : "var(--surface-soft,#f9fafb)",
                border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700,
                color: hasSel ? cat.color : "var(--text,#111)",
              }}
            >
              <span style={{ fontSize: 15 }}>{cat.icon}</span>
              <span style={{ flex: 1, textAlign: "left" }}>{cat.label}</span>
              {hasSel && <span style={{ fontSize: 10, color: cat.color }}>✓</span>}
              <span style={{ fontSize: 9, color: "var(--muted)" }}>{isOpen ? "▲" : "▼"}</span>
            </button>

            {isOpen && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5, padding: "7px 11px", background: "var(--panel,#fff)" }}>
                {cat.subs.map(sub => (
                  <button
                    key={sub.value} type="button"
                    onClick={() => onChange?.(sub.value)}
                    style={{
                      padding: "4px 11px", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer",
                      border: value === sub.value ? `2px solid ${cat.color}` : "2px solid transparent",
                      background: value === sub.value ? `${cat.color}18` : "var(--surface-soft,#f5f5f5)",
                      color: value === sub.value ? cat.color : "var(--muted,#6b7280)",
                      transition: "all .12s",
                    }}
                  >
                    {sub.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────

export default function ClimateZonePanel({ user, fieldId, fieldGeometry, onToast }) {
  const [zones,           setZones]          = useState([]);
  const [loading,         setLoading]        = useState(true);
  const [modalOpen,       setModalOpen]      = useState(false);
  const [saving,          setSaving]         = useState(false);
  const [selectedId,      setSelectedId]     = useState(null);
  const [confirmId,       setConfirmId]      = useState(null);
  const [selectedZoneData, setSelectedZoneData] = useState(null); // { id, geom, info, name }
  const [navTarget,       setNavTarget]      = useState(null);

  // Form state
  const [fName,     setFName]     = useState("");
  const [fDesc,     setFDesc]     = useState("");
  const [fHa,       setFHa]       = useState("");
  const [fSubType,  setFSubType]  = useState("");
  const [fRisk,     setFRisk]     = useState("LOW");
  const [fGeometry, setFGeometry] = useState(null);

  // ── Load ─────────────────────────────────────────────────────────────────────
  // ── localStorage geometry cache helpers ──────────────────────────────────────
  function geoCacheKey() { return `cz_geom_f${fieldId}`; }
  function geoReadCache() {
    try { return JSON.parse(localStorage.getItem(geoCacheKey()) || "{}"); } catch { return {}; }
  }
  function geoWriteCache(zoneId, geom) {
    try {
      const c = geoReadCache();
      c[String(zoneId)] = geom;
      localStorage.setItem(geoCacheKey(), JSON.stringify(c));
    } catch {}
  }
  function geoDeleteCache(zoneId) {
    try {
      const c = geoReadCache();
      delete c[String(zoneId)];
      localStorage.setItem(geoCacheKey(), JSON.stringify(c));
    } catch {}
  }

  const loadZones = useCallback(async () => {
    if (!fieldId || !user) return null;
    setLoading(true);
    try {
      const res = await apiFetch(user, `/api/climate-zones/field/${fieldId}`);
      if (!res.ok) { setZones([]); return null; }
      const raw = await res.json();
      const list = Array.isArray(raw) ? raw : (raw?.items ?? []);
      const cache = geoReadCache();
      const merged = list.map(z => ({
        ...z,
        _cachedGeom: cache[String(z.id)] ?? null,
      }));
      setZones(merged);

      // Background-prefetch geometry for zones that have no cached copy and
      // whose list-response didn't include geometry — so the map is ready
      // before the user taps a zone (avoids the "unavailable" overlay).
      const missing = merged.filter(z => !z._cachedGeom && !extractGeometry(z));
      missing.forEach(z => {
        apiFetch(user, `/api/climate-zones/${z.id}`)
          .then(r => r.ok ? r.json() : null)
          .then(data => {
            if (!data) return;
            const geom = extractGeometry(data);
            if (geom) {
              geoWriteCache(String(z.id), geom);
              setZones(prev => prev.map(pz =>
                String(pz.id) === String(z.id) ? { ...pz, _cachedGeom: geom } : pz
              ));
            }
          })
          .catch(() => {});
      });

      return list;
    } catch { setZones([]); return null; }
    finally { setLoading(false); }
  }, [fieldId, user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadZones(); }, [loadZones]);

  // ── Show geometry for selected zone ──────────────────────────────────────────
  useEffect(() => {
    if (!selectedId) { setSelectedZoneData(null); return; }

    const zone = zones.find(z => z.id === selectedId || String(z.id) === String(selectedId));
    if (!zone) return;
    const info = getSubInfo(zone.climatSubType);

    function apply(geom) {
      console.log("[CZ] applying geometry for zone", selectedId, geom?.type);
      setSelectedZoneData({ id: selectedId, geom, info, name: zone.name });
    }

    // 1. Try geometry from list response (some backends include it)
    const listGeom = extractGeometry(zone);
    if (listGeom) { apply(listGeom); return; }

    // 2. Try localStorage cache
    const cachedRaw = zone._cachedGeom ?? geoReadCache()[String(selectedId)];
    if (cachedRaw) {
      const geom = unwrapGeom(tryParseJson(cachedRaw));
      if (geom) { apply(geom); return; }
    }

    // 3. Fetch single zone — GET /api/climate-zones/{id}
    apiFetch(user, `/api/climate-zones/${selectedId}`)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => {
        console.log("[CZ] single-zone fetch keys:", Object.keys(data || {}));
        const geom = extractGeometry(data);
        if (geom) {
          geoWriteCache(selectedId, geom);
          apply(geom);
        } else {
          console.warn("[CZ] no geometry in single-zone response, full data:", data);
          setSelectedZoneData({ id: selectedId, geom: null, info, name: zone.name });
        }
      })
      .catch(err => {
        console.warn("[CZ] single-zone fetch failed:", err);
        setSelectedZoneData({ id: selectedId, geom: null, info, name: zone.name });
      });
  }, [selectedId, zones, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Toast ─────────────────────────────────────────────────────────────────────
  function toast(type, msg) {
    if (onToast) onToast(type, msg);
  }

  // ── Save ─────────────────────────────────────────────────────────────────────
  async function save(e) {
    e.preventDefault();
    if (!fName.trim()) { toast("err", "⚠️ Вкажіть назву зони"); return; }
    if (!fSubType)     { toast("err", "⚠️ Оберіть тип загрози"); return; }
    if (!fGeometry)    { toast("err", "⚠️ Намалюйте зону на карті"); return; }

    // Snapshot before any async ops
    const geomSnapshot = fGeometry;
    const nameSnapshot = fName.trim();

    setSaving(true);
    try {
      const body = {
        name:          nameSnapshot,
        description:   fDesc.trim() || "—",
        ha:            fHa ? Number(fHa) : 0.1,
        fieldId:       Number(fieldId),
        climatSubType: fSubType,
        riskLevel:     fRisk,
        geometry:      geomSnapshot,
      };

      const res = await apiFetch(user, "/api/climate-zones", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        toast("err", txt || `Помилка ${res.status}`);
        return;
      }

      // Try to get id from POST response (multiple possible structures)
      let cachedById = false;
      try {
        const created = await res.json();
        console.log("[CZ] POST response:", created);
        const id = created?.id ?? created?.data?.id ?? created?.zoneId ?? created?.result?.id;
        if (id != null) {
          geoWriteCache(id, geomSnapshot); // cache BEFORE loadZones so _cachedGeom is included
          cachedById = true;
          console.log("[CZ] cached geometry for new zone id:", id);
        }
      } catch { /* empty response body — that's fine */ }

      // Reload zones list to get the new zone
      const freshList = await loadZones();

      // Fallback: if POST response had no id, find zone by name and cache (then reload again)
      if (!cachedById && freshList && geomSnapshot) {
        const newZone = freshList.find(z => z.name === nameSnapshot);
        if (newZone?.id != null) {
          geoWriteCache(newZone.id, geomSnapshot);
          console.log("[CZ] cached geometry for new zone by name, id:", newZone.id);
          await loadZones(); // reload again so _cachedGeom is included in state
        }
      }

      resetForm();
      setModalOpen(false);
      toast("ok", "✅ Кліматичну зону збережено");
    } catch (err) {
      console.error(err);
      toast("err", "Помилка збереження");
    } finally {
      setSaving(false);
    }
  }

  async function deleteZone(id) {
    try {
      await apiFetch(user, `/api/climate-zones/${id}`, { method: "DELETE" });
      geoDeleteCache(id);
      setZones(prev => prev.filter(z => z.id !== id));
      if (selectedId === id) { setSelectedId(null); setSelectedZoneData(null); }
      toast("ok", "🗑 Зону видалено");
    } catch { toast("err", "Помилка видалення"); }
    finally { setConfirmId(null); }
  }

  function resetForm() {
    setFName(""); setFDesc(""); setFHa(""); setFSubType(""); setFRisk("LOW"); setFGeometry(null);
  }

  function handleNavigate(zone) {
    const geom = zone._cachedGeom
      ? unwrapGeom(tryParseJson(zone._cachedGeom))
      : extractGeometry(zone);
    const sub = getSubInfo(zone.climatSubType);
    let lat = 48, lng = 31;
    if (geom) {
      const c = getFieldCenter({ type: "Feature", geometry: geom });
      lng = c[0]; lat = c[1];
    }
    setNavTarget({ lat, lng, label: zone.name, color: sub.color, polygon: geom ?? null });
  }

  // ── Stats ────────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const byRisk = { LOW: 0, MEDIUM: 0, HIGH: 0 };
    zones.forEach(z => { if (byRisk[z.riskLevel] !== undefined) byRisk[z.riskLevel]++; });
    return byRisk;
  }, [zones]);

  // Active category color for DrawMap — also resets drawn geometry when category changes
  const drawColor = useMemo(() => {
    if (!fSubType) return "#ef4444";
    return getSubInfo(fSubType).color;
  }, [fSubType]);

  useEffect(() => {
    if (fSubType) setFGeometry(null); // remount DrawMap with new color → user redraws
  }, [fSubType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Active categories present in zone list (for legend)
  const activeCats = useMemo(() =>
    CLIMAT_CATEGORIES.filter(cat =>
      zones.some(z => cat.subs.some(s => s.value === z.climatSubType))
    ),
  [zones]);

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="panel" style={{ marginTop: 14 }}>
      <style>{`
        @keyframes czSpin { to { transform: rotate(360deg); } }
        .cz-popup .mapboxgl-popup-content {
          padding: 8px 12px;
          border-radius: 9px;
          font-size: 12px;
          box-shadow: 0 4px 16px rgba(0,0,0,.18);
          background: var(--panel, #fff);
          color: var(--text, #111);
          border: 1px solid var(--border, #e5e7eb);
          min-width: 100px;
        }
        .cz-popup .mapboxgl-popup-tip { border-top-color: var(--panel, #fff); }
      `}</style>

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="fd-sectionHead" style={{ marginBottom: 14 }}>
        <div>
          <div className="fd-sectionTitle">🌦 Кліматичні зони</div>
          <div className="fd-sectionSub" style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            {loading ? "Завантаження..." : (
              <>
                <span style={{ color: "var(--muted)" }}>{zones.length} {zones.length === 1 ? "зона" : zones.length < 5 ? "зони" : "зон"}</span>
                {stats.HIGH   > 0 && <span style={{ color: "#ef4444", fontWeight: 700, fontSize: 12 }}>● Високий: {stats.HIGH}</span>}
                {stats.MEDIUM > 0 && <span style={{ color: "#f59e0b", fontWeight: 700, fontSize: 12 }}>● Середній: {stats.MEDIUM}</span>}
                {stats.LOW    > 0 && <span style={{ color: "#22c55e", fontWeight: 700, fontSize: 12 }}>● Низький: {stats.LOW}</span>}
              </>
            )}
          </div>
        </div>
        <button
          className="btn primary"
          type="button"
          onClick={() => { resetForm(); setModalOpen(true); }}
          disabled={loading}
        >
          + Нова зона
        </button>
      </div>

      {/* ── Loading spinner ─────────────────────────────────────────────────── */}
      {loading && (
        <div style={{ textAlign: "center", padding: "56px 0", color: "var(--muted)" }}>
          <div style={{ width: 32, height: 32, margin: "0 auto", border: "3px solid var(--border)", borderTop: "3px solid #ef4444", borderRadius: "50%", animation: "czSpin .75s linear infinite" }} />
          <p style={{ marginTop: 12, fontSize: 13 }}>Завантаження зон...</p>
        </div>
      )}

      {/* ── Main grid: map + list ───────────────────────────────────────────── */}
      {!loading && (
        <div style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 16,
          alignItems: "flex-start",
        }}>

          {/* Map column */}
          {fieldGeometry && (
            <div style={{ flex: "1 1 300px", minWidth: 0 }}>
              <div style={{ height: 400 }}>
                <ViewMap
                  fieldGeoJson={fieldGeometry}
                  selectedZoneData={selectedZoneData}
                  onRetry={selectedId ? () => {
                    // Re-trigger the geometry lookup effect by resetting and re-selecting
                    const id = selectedId;
                    setSelectedZoneData(null);
                    setSelectedId(null);
                    setTimeout(() => setSelectedId(id), 60);
                  } : undefined}
                />
              </div>

              {/* Category legend */}
              {activeCats.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 8 }}>
                  {activeCats.map(cat => (
                    <span
                      key={cat.value}
                      style={{
                        fontSize: 10, padding: "2px 8px", borderRadius: 20, fontWeight: 700,
                        background: `${cat.color}15`,
                        border: `1.5px solid ${cat.color}40`,
                        color: cat.color,
                        display: "flex", alignItems: "center", gap: 3,
                      }}
                    >
                      <span style={{
                        width: 7, height: 7, borderRadius: "50%",
                        background: cat.color, display: "inline-block", flexShrink: 0,
                      }} />
                      {cat.icon} {cat.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Zone list column */}
          <div style={{ flex: "1 1 240px", minWidth: 0 }}>
            {zones.length === 0 ? (
              <div style={{
                textAlign: "center", padding: "40px 20px",
                border: "1px dashed var(--border)", borderRadius: 12,
              }}>
                <div style={{ fontSize: 36 }}>🌦</div>
                <p style={{ margin: "8px 0 4px", fontSize: 14, fontWeight: 700, color: "var(--text)" }}>
                  Немає зон
                </p>
                <p style={{ margin: 0, fontSize: 12, color: "var(--muted)" }}>
                  Натисніть «+ Нова зона» щоб позначити ризикову ділянку
                </p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                {zones.map(z => (
                  <ZoneCard
                    key={z.id}
                    zone={z}
                    selected={selectedId === z.id}
                    onSelect={id => setSelectedId(prev => {
                      const next = prev === id ? null : id;
                      if (next === null) setSelectedZoneData(null);
                      return next;
                    })}
                    onDelete={id => setConfirmId(id)}
                    onNavigate={handleNavigate}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Create modal ─────────────────────────────────────────────────────── */}
      {modalOpen && createPortal(
        <div className="fd-modal" onClick={() => setModalOpen(false)}>
          <div
            className="fd-modalCard"
            style={{ width: "min(700px, 96vw)" }}
            onClick={e => e.stopPropagation()}
          >
            <div className="fd-modalHead">
              <div>
                <div className="fd-modalTitle">🌦 Нова кліматична зона</div>
                <div className="fd-modalSub">Оберіть тип загрози і намалюйте зону на карті</div>
              </div>
              <button className="btn" type="button" onClick={() => setModalOpen(false)}>✕</button>
            </div>

            <form className="fd-modalBody" onSubmit={save}>

              {/* Назва */}
              <div className="fd-fg" style={{ marginBottom: 12 }}>
                <div className="fd-lbl">Назва зони <span style={{ color: "#ef4444" }}>*</span></div>
                <input
                  className="fd-inputXL"
                  value={fName}
                  onChange={e => setFName(e.target.value)}
                  placeholder="Напр.: Низина з підтопленням, Відкрита ділянка..."
                  maxLength={100}
                  autoFocus
                />
                <div style={{ fontSize: 10, textAlign: "right", marginTop: 2, color: fName.length > 80 ? "#ea580c" : "var(--muted)" }}>
                  {fName.length}/100
                </div>
              </div>

              {/* Тип загрози */}
              <div className="fd-fg" style={{ marginBottom: 12 }}>
                <div className="fd-lbl">Тип загрози <span style={{ color: "#ef4444" }}>*</span></div>
                <SubTypePicker value={fSubType} onChange={setFSubType} />
              </div>

              {/* Рівень ризику */}
              <div className="fd-fg" style={{ marginBottom: 12 }}>
                <div className="fd-lbl">Рівень ризику</div>
                <div style={{ display: "flex", gap: 7 }}>
                  {RISK_LEVELS.map(r => (
                    <button
                      key={r.value}
                      type="button"
                      onClick={() => setFRisk(r.value)}
                      style={{
                        flex: 1, padding: "8px 0", borderRadius: 9, fontWeight: 700, fontSize: 12, cursor: "pointer",
                        border: fRisk === r.value ? `2px solid ${r.color}` : "2px solid transparent",
                        background: fRisk === r.value ? r.bg : "var(--surface-soft,#f5f5f5)",
                        color: fRisk === r.value ? r.color : "var(--muted,#6b7280)",
                        transition: "all .12s",
                      }}
                    >
                      {fRisk === r.value ? "● " : "○ "}{r.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Площа + Опис */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 12, marginBottom: 12 }}>
                <div className="fd-fg">
                  <div className="fd-lbl">Площа (га)</div>
                  <input
                    className="fd-inputXL"
                    type="number"
                    min="0"
                    step="any"
                    value={fHa}
                    onChange={e => setFHa(e.target.value)}
                    placeholder="Напр: 2.5"
                  />
                </div>
                <div className="fd-fg">
                  <div className="fd-lbl">Опис</div>
                  <input
                    className="fd-inputXL"
                    value={fDesc}
                    onChange={e => setFDesc(e.target.value)}
                    placeholder="Короткий опис ризику..."
                    maxLength={500}
                  />
                </div>
              </div>

              {/* Карта */}
              <div className="fd-fg" style={{ marginBottom: 14 }}>
                <div className="fd-lbl" style={{ marginBottom: 5, display: "flex", alignItems: "center", gap: 6 }}>
                  <span>Зона на карті <span style={{ color: "#ef4444" }}>*</span></span>
                  {fGeometry && (
                    <span style={{ fontSize: 10, color: "#22c55e", fontWeight: 700 }}>✓ Намальовано</span>
                  )}
                  {fSubType && (
                    <span style={{
                      fontSize: 10, padding: "1px 7px", borderRadius: 20, fontWeight: 700,
                      background: `${drawColor}18`, border: `1px solid ${drawColor}40`, color: drawColor,
                    }}>
                      {getSubInfo(fSubType).catIcon} {getSubInfo(fSubType).catLabel}
                    </span>
                  )}
                </div>
                <DrawMap
                  key={drawColor}
                  fieldGeoJson={fieldGeometry}
                  onGeometryChange={setFGeometry}
                  accentColor={drawColor}
                />
              </div>

              <div className="fd-stickyActions">
                <button className="btn" type="button" onClick={() => setModalOpen(false)} disabled={saving}>
                  Скасувати
                </button>
                <button className="btn primary" type="submit" disabled={saving}>
                  {saving ? "Зберігаю..." : "💾 Зберегти зону"}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* ── Confirm delete ───────────────────────────────────────────────────── */}
      {confirmId !== null && createPortal(
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 6000, padding: "16px" }}
          onClick={() => setConfirmId(null)}
        >
          <div
            style={{ background: "var(--modal-bg,var(--panel,#fff))", border: "1px solid var(--border)", borderRadius: 16, padding: "24px 20px", width: "min(340px, 100%)", textAlign: "center", boxShadow: "0 8px 40px rgba(0,0,0,.2)", color: "var(--text)" }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontSize: 28, marginBottom: 10 }}>🗑️</div>
            <p style={{ margin: "0 0 20px", fontSize: 14, lineHeight: 1.5 }}>Видалити цю кліматичну зону?</p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
              <button
                onClick={() => deleteZone(confirmId)}
                style={{ background: "#dc2626", color: "#fff", border: "none", padding: "9px 20px", borderRadius: 9, cursor: "pointer", fontSize: 14, fontWeight: 700, minHeight: 44, flex: 1 }}
              >
                Так, видалити
              </button>
              <button
                onClick={() => setConfirmId(null)}
                style={{ background: "transparent", color: "var(--muted)", border: "1px solid var(--border)", padding: "9px 20px", borderRadius: 9, cursor: "pointer", fontSize: 14, minHeight: 44, flex: 1 }}
              >
                Скасувати
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {navTarget && (
        <ClimateNavigateMap
          fieldGeoJson={fieldGeometry}
          target={navTarget}
          onClose={() => setNavTarget(null)}
        />
      )}
    </div>
  );
}
